import { EventEmitter, Injectable } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
// scanner-disable-next-line R7 -- getSvgContent fetches static assets/ SVGs; the interceptor deliberately skips assets/ URLs
import { HttpClient } from '@angular/common/http';
import { Observable, map, shareReplay } from 'rxjs';

export type { WidgetExportFormat } from 'src/app/api/serialize/widget.serialize';

/**
 * Widget client-side concerns: the shared resize event and the static SVG
 * asset cache. All backend transport lives in `WidgetApiService`; the
 * HttpClient here fetches only bundled `assets/` files, which the
 * interceptor deliberately leaves untouched.
 */
@Injectable({
  providedIn: 'root',
})
export class WidgetService {
  widgetResized = new EventEmitter<void>();
  private svgCache = new Map<string, Observable<SafeHtml>>();

  constructor(
    private http: HttpClient,
    private sanitizer: DomSanitizer,
  ) {}

  getSvgContent(
    svgPath: string,
    width = 70,
    height = 70,
  ): Observable<SafeHtml> {
    const cacheKey = `${svgPath}|${width}x${height}`;
    if (!this.svgCache.has(cacheKey)) {
      const svgContent$ = this.http.get(svgPath, { responseType: 'text' }).pipe(
        map((svg) => {
          const doc = new DOMParser().parseFromString(svg, 'image/svg+xml');
          const root = doc.documentElement as unknown as SVGSVGElement;

          // Convert to rem using your global helper
          const wRem = `${px2Rem(width)}rem`;
          const hRem = `${px2Rem(height)}rem`;

          root.setAttribute('width', wRem);
          root.setAttribute('height', hRem);

          const xml = new XMLSerializer().serializeToString(root);
          return this.sanitizer.bypassSecurityTrustHtml(xml);
        }),
        shareReplay(1),
      );

      this.svgCache.set(cacheKey, svgContent$);
    }

    return this.svgCache.get(cacheKey)!;
  }
}
